import SliderField from "./SliderField";
import { PARAM_RANGES } from "../geometry/defaults";
import "./Sidebar.css";

const GEOMETRY_KEYS = ["sod", "sdd", "tiltX", "tiltY"];

function DeltaBadge({ pct }) {
  const sign = pct > 0 ? "+" : "";
  const color = pct >= 0 ? "#5EEAD4" : "#F97362";
  return (
    <span className="fov-stat-delta" style={{ color }}>
      {sign}
      {pct.toFixed(1)}%
    </span>
  );
}

export default function Sidebar({
  params,
  onParamChange,
  showInclined,
  showCoplanar,
  showGimbal,
  onToggleInclined,
  onToggleCoplanar,
  onToggleGimbal,
  fovResults,
  relativeToCoplanar,
  theta,
  onThetaChange,
  onReset,
  showAllFootprints,
  onToggleAllFootprints,
  onFocusFov,
  onFocusOverall,
}) {
  return (
    <aside className="sidebar">
      <div className="sidebar-fixed">
        <section className="sidebar-section">
          <h3 className="sidebar-heading">Setups</h3>
          <label className="checkbox-row">
            <input type="checkbox" checked={showCoplanar} onChange={onToggleCoplanar} />
            <span className="swatch swatch-coplanar" />
            Coplanar laminography
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={showInclined} onChange={onToggleInclined} />
            <span className="swatch swatch-inclined" />
            Inclined laminography
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={showGimbal} onChange={onToggleGimbal} />
            <span className="swatch swatch-gimbal" />
            Gimbal lock laminography
          </label>
        </section>

        <section className="sidebar-section">
          <h3 className="sidebar-heading">FOV Comparison</h3>
          <div className="fov-stat">
            <span className="fov-stat-label">Coplanar area</span>
            <span className="fov-stat-value">{fovResults.coplanar.area.toFixed(4)} mm²</span>
            <span className="fov-stat-delta fov-stat-delta-base">base</span>
          </div>
          <div className="fov-stat">
            <span className="fov-stat-label">Inclined area</span>
            <span className="fov-stat-value">{fovResults.inclined.area.toFixed(4)} mm²</span>
            <DeltaBadge pct={relativeToCoplanar.inclined} />
          </div>
          <div className="fov-stat">
            <span className="fov-stat-label">Gimbal lock area</span>
            <span className="fov-stat-value">{fovResults.gimbal.area.toFixed(4)} mm²</span>
            <DeltaBadge pct={relativeToCoplanar.gimbal} />
          </div>
          <label className="checkbox-row checkbox-row-secondary">
            <input
              type="checkbox"
              checked={showAllFootprints}
              onChange={onToggleAllFootprints}
            />
            Show all footprints
          </label>
          <div className="view-button-row">
            <button type="button" className="view-button" onClick={onFocusFov}>
              FOV
            </button>
            <button type="button" className="view-button" onClick={onFocusOverall}>
              Overall
            </button>
          </div>
        </section>
      </div>

      <div className="sidebar-scroll">
        <section className="sidebar-section">
          <h3 className="sidebar-heading">Rotation</h3>
          <SliderField
            label="Theta"
            unit="deg"
            value={theta}
            min={0}
            max={360 - 360 / params.numAngles}
            step={360 / params.numAngles}
            onChange={onThetaChange}
          />
        </section>

        <section className="sidebar-section">
          <h3 className="sidebar-heading">Geometry</h3>
          {GEOMETRY_KEYS.map((key) => (
            <SliderField
              key={key}
              label={PARAM_RANGES[key].label}
              unit={PARAM_RANGES[key].unit}
              value={params[key]}
              min={PARAM_RANGES[key].min}
              max={PARAM_RANGES[key].max}
              step={PARAM_RANGES[key].step}
              onChange={(v) => onParamChange(key, v)}
            />
          ))}
        </section>

        <section className="sidebar-section">
          <h3 className="sidebar-heading">Detector</h3>
          <div className="slider-card-header">
            <span className="slider-card-label">Width × Height</span>
            <span className="slider-card-unit">{PARAM_RANGES.detWidth.unit}</span>
          </div>
          <div className="slider-row">
            <SliderField
              compact
              prefix="W"
              label=""
              value={params.detWidth}
              min={PARAM_RANGES.detWidth.min}
              max={PARAM_RANGES.detWidth.max}
              step={PARAM_RANGES.detWidth.step}
              onChange={(v) => onParamChange("detWidth", v)}
            />
            <SliderField
              compact
              prefix="H"
              label=""
              value={params.detHeight}
              min={PARAM_RANGES.detHeight.min}
              max={PARAM_RANGES.detHeight.max}
              step={PARAM_RANGES.detHeight.step}
              onChange={(v) => onParamChange("detHeight", v)}
            />
          </div>

          <SliderField
            label={PARAM_RANGES.detPitch.label}
            unit={PARAM_RANGES.detPitch.unit}
            value={params.detPitch}
            min={PARAM_RANGES.detPitch.min}
            max={PARAM_RANGES.detPitch.max}
            step={PARAM_RANGES.detPitch.step}
            onChange={(v) => onParamChange("detPitch", v)}
          />

          <div className="slider-card-header">
            <span className="slider-card-label">Detector Offset</span>
            <span className="slider-card-unit">{PARAM_RANGES.offsetU.unit}</span>
          </div>
          <div className="slider-row">
            <SliderField
              compact
              prefix="U"
              label=""
              value={params.offsetU}
              min={PARAM_RANGES.offsetU.min}
              max={PARAM_RANGES.offsetU.max}
              step={PARAM_RANGES.offsetU.step}
              onChange={(v) => onParamChange("offsetU", v)}
            />
            <SliderField
              compact
              prefix="V"
              label=""
              value={params.offsetV}
              min={PARAM_RANGES.offsetV.min}
              max={PARAM_RANGES.offsetV.max}
              step={PARAM_RANGES.offsetV.step}
              onChange={(v) => onParamChange("offsetV", v)}
            />
          </div>
        </section>

        <section className="sidebar-section">
          <h3 className="sidebar-heading">Acquisition</h3>
          <SliderField
            label={PARAM_RANGES.numAngles.label}
            unit={PARAM_RANGES.numAngles.unit}
            value={params.numAngles}
            min={PARAM_RANGES.numAngles.min}
            max={PARAM_RANGES.numAngles.max}
            step={PARAM_RANGES.numAngles.step}
            onChange={(v) => onParamChange("numAngles", v)}
          />
        </section>

        <button type="button" className="reset-button" onClick={onReset}>
          Reset to Defaults
        </button>
      </div>
    </aside>
  );
}
